import { Link } from "wouter";
import { LogOut, Users, ShieldCheck } from "lucide-react";
import { INSTITUTIONS_ENABLED } from "@shared/constants/features";
import { useCurrentUser, useLogout } from "../lib/auth/useAuth";
import { BrandWordmark } from "./BrandWordmark";
import { OrgSwitcher } from "./OrgSwitcher";
import { NotificationBell } from "./notifications/NotificationBell";
import { Button } from "./ui/Button";
import { FeatureSlot } from "../features/_sim/Slot"; // SIM-SEAM: v2 nav pills + header actions
import { cn } from "../lib/cn";
import { HEADER_GUTTER, PAGE_WIDTH } from "../lib/layout";

// The header row always spans the wide layout (lib/layout.ts), so from sm up
// the logo lines up with the left edge of a wide page.
const ROW = cn("mx-auto flex items-center justify-between gap-2 py-3", HEADER_GUTTER, PAGE_WIDTH.wide);

/** Sticky top bar shared by the logged-in pages: frosted glass over the page's scene. */
export function AppHeader() {
  const { data: user } = useCurrentUser();
  const logout = useLogout();
  return (
    <header className="sticky top-0 z-40 border-b border-white/70 bg-white/70 shadow-[0_8px_30px_-22px_rgb(2_132_199/0.35)] backdrop-blur-md">
      <div className={ROW}>
        <Link href="/dashboard" className="flex shrink-0 items-center gap-2">
          <BrandWordmark className="h-6" />
        </Link>
        {/* SIM-SEAM: desktop nav pills */}
        <FeatureSlot name="header.nav" />
        <div className="flex items-center gap-2 sm:gap-3">
          {INSTITUTIONS_ENABLED && <OrgSwitcher />}
          {/* SIM-SEAM: cart + help buttons */}
          <FeatureSlot name="header.actions" />
          <NotificationBell />
          {user?.currentOrganizationId && (
            <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
              <Link href="/org/admin">
                <Users className="h-4 w-4" aria-hidden /> Team
              </Link>
            </Button>
          )}
          {user?.isPlatformAdmin && (
            <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
              <Link href="/admin">
                <ShieldCheck className="h-4 w-4" aria-hidden /> Console
              </Link>
            </Button>
          )}
          {user?.fullName && (
            <span className="hidden text-sm text-neutral-600 sm:block">{user.fullName}</span>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => logout.mutate()}
            isLoading={logout.isPending}
            aria-label="Sign out"
            className="shrink-0"
          >
            {!logout.isPending && <LogOut className="h-4 w-4" aria-hidden />}
            <span className="hidden sm:inline">
              {logout.isPending ? "Signing out…" : "Sign out"}
            </span>
          </Button>
        </div>
      </div>
    </header>
  );
}
